import { m } from '@hub/messages';
import { currentRelativePath, isAuthenticatedLocation, localizedLoginHref, localizedLogoutConfirmationHref } from '@hub/router';
import type { RouterAuthState } from '@hub/router/routerAuth';
import { getErrorMessage, isAuthRedirectError, logoutMutation, type UserInfo } from '@platform/api';
import { useEvaluationContext } from '@platform/feature-flags';
import { setObservabilityUser } from '@platform/observability';
import { setTrackingUser, track, trackBeforeNav } from '@platform/tracking';
import { LoadingOverlay } from '@platform/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef } from 'react';
import { AuthContext } from './AuthContext';
import type { AuthState } from './auth.types';
import { isLogoutConfirmationHidden } from './logoutConfirmation.storage';
import { sessionQueryOptions } from './session.queryOptions';
import { sessionSurvivesError } from './sessionSurvivesError';

/**
 * Maps query state to session state. `isPending`, not `isLoading`: the latter reads false
 * while a fetch is paused (offline, the only cause with networkMode 'online'), and that
 * no-data-no-error state is not an anonymous one.
 */
function sessionState(probe: {
  isPending: boolean;
  isAnonymous: boolean;
  user: UserInfo | null;
  error: unknown;
}): RouterAuthState<UserInfo> {
  if (probe.isPending) {
    return { status: 'loading' };
  }
  if (probe.isAnonymous) {
    return { status: 'anonymous' };
  }
  if (probe.user != null) {
    return { status: 'authenticated', user: probe.user };
  }
  // Lost the identity without a 401: auth is unknown, so error page instead of login. WWP-534.
  if (probe.error != null) {
    return { status: 'unresolved' };
  }
  // Settled, no error, no usable payload: nobody is signed in.
  return { status: 'anonymous' };
}

/** `sessionStorage.clear()` that tolerates unavailable storage. */
function clearSessionStorage() {
  try {
    sessionStorage.clear();
  } catch {
    // Storage unavailable: nothing to clean up.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // The probe unwraps to the user and throws on failure: a 401 is an anonymous caller (ApiError(401)),
  // anything else is an unknown auth state.
  const { data, isPending, error } = useQuery(sessionQueryOptions);
  const [_, setFeatureFlagContext] = useEvaluationContext();
  const queryClient = useQueryClient();

  const cachedUser = data ?? null;
  // Only a terminal 401 is an authoritative "anonymous".
  const isAnonymous = isAuthRedirectError(error);
  const sessionSurvives = sessionSurvivesError(error);
  const user = !isAnonymous && sessionSurvives ? cachedUser : null;

  useEffect(() => {
    setObservabilityUser(user?.userHandle ? { userHandle: user.userHandle, name: user.name, email: user.email } : null);
    setFeatureFlagContext({ userId: user?.userHandle });
    setTrackingUser(user?.userHandle ?? null);
  }, [user, setFeatureFlagContext]);

  // Identity from the session probe alone — not the error-folding `user` — so a transient refetch
  // error (which keeps stale data) isn't read as a logout. React Query retains the last-success `data`
  // on error, so a terminal 401 does NOT clear it; fold `isAnonymous` to null explicitly here, else the
  // stale handle survives, `previousUserHandle` still matches, and identity-scoped queries are never
  // evicted on session expiry.
  const settled = data !== undefined || isAnonymous;
  const sessionUserHandle = !settled ? undefined : isAnonymous ? null : (data?.userHandle ?? null);

  // The probe can refetch in place to a different (or absent) user without a full
  // reload, so drop identity-scoped queries when it changes.
  const previousUserHandle = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (sessionUserHandle === undefined) {
      return;
    }
    if (previousUserHandle.current !== undefined && previousUserHandle.current !== sessionUserHandle) {
      queryClient.removeQueries({ predicate: (query) => query.meta?.sharedAcrossIdentities !== true });
      // Only when a real user is being replaced, else the leftovers are the visitor's own: an
      // anonymous -> authenticated transition would sweep pre-login tickets (see appleLinkIntent.storage).
      if (previousUserHandle.current !== null) {
        clearSessionStorage();
      }
    }
    previousUserHandle.current = sessionUserHandle;
  }, [sessionUserHandle, queryClient]);

  const { mutate: logout, isPending: isLoggingOut } = useMutation({
    ...logoutMutation(),
    onSuccess: (body) => {
      // The nav happens before the session probe can refetch, so the identity-change effect never runs here.
      clearSessionStorage();
      window.location.href = body.logoutUrl;
    },
    onError: (logoutError) => {
      const reason = getErrorMessage(logoutError);
      track('logout_failed', { reason });
      // biome-ignore lint/suspicious/noConsole: temporary until proper user notification (WWP-40)
      console.error(reason);
    },
  });

  const handleLogout = () => {
    trackBeforeNav('logout', {});
    const currentPath = currentRelativePath();
    const isAuthed = isAuthenticatedLocation();
    // Always show the (public, unguarded) logout confirmation screen unless dismissed.
    // If dismissed, returning to a protected page while logged out trips the auth guard
    // and fires a phantom login_started; land on the login page instead. Public pages: stay put.
    // Both carry the page being left as `to`, so re-login lands back on it instead of falling
    // back to the (flag-gated) default home.
    const returnTo = !isLogoutConfirmationHidden()
      ? localizedLogoutConfirmationHref(isAuthed ? currentPath : undefined)
      : isAuthed
        ? localizedLoginHref(currentPath)
        : currentPath;
    logout({ query: { returnTo } });
  };

  const value: AuthState = {
    ...sessionState({ isPending, isAnonymous, user, error }),
    isLoggingOut,
    logout: handleLogout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <LoadingOverlay open={isLoggingOut} label={m.auth_logout_pending()} />
    </AuthContext.Provider>
  );
}
